import { Fragment, useMemo } from 'react';
// Models
import { IUsage } from 'app/store/BillingUsage/BillingUsage.models';
// Mui
import { Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Theme } from '@mui/material';
// Utils
import { toNumberWithCommas } from 'app/utilities/Utilities';
// 
import InvoiceNumberChips from './InvoiceNumberChips';
// i18next
import { useTranslation } from 'react-i18next';
import useGroupBySubscriptionServiceInvoice from './useGroupBySubscriptionServiceInvoice';

type Props = {
  data: IUsage[];
  includedInvoiceStatuses?: string[];
}

export default function UsageBySubscriptionServiceInvoice({ data, includedInvoiceStatuses }: Props) {
  const { t } = useTranslation();

  const groupBySubscriptionServiceInvoice = useGroupBySubscriptionServiceInvoice(data, includedInvoiceStatuses);

  const totalAmount = useMemo(() => {
    return groupBySubscriptionServiceInvoice.reduce((acc, subscriptionData) => {
      const serviceTotal = subscriptionData.services.reduce((caseSum, service) => caseSum + service.amount, 0);
      return acc + serviceTotal;
    }, 0);
  }, [groupBySubscriptionServiceInvoice]);

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{t('labels.subscription')}</TableCell>
            <TableCell sx={{ width: '33.33%' }}>{t('labels.serviceName')}</TableCell>
            <TableCell>{t('labels.invoices')}</TableCell>
            <TableCell>{t('labels.qty')}</TableCell>
            <TableCell>{t('labels.total')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupBySubscriptionServiceInvoice.map((group) => (
            <Fragment key={`subscriptionItem-${group.id}`}>
              <TableRow>
                <TableCell rowSpan={group.services.length + 1}>{group.name}</TableCell>
              </TableRow>
              {group.services.map((service) => (
                <TableRow key={`${group.id}-serviceItem-${service.id}`}>
                  <TableCell>{service.name}</TableCell>
                  <TableCell>
                    <InvoiceNumberChips invoices={service.invoices || []} />
                  </TableCell>
                  <TableCell>{service.quantity}</TableCell>
                  <TableCell>{`$${toNumberWithCommas(service.amount)}`}</TableCell>
                </TableRow>
              ))}
            </Fragment>
          ))}
        </TableBody>
        <TableFooter sx={{ position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1 }}>
          <TableRow sx={{
            bgcolor: (theme:Theme) => theme.palette.grey[50],
            '& > td': {
              borderBottom: 0
            }
          }}>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} colSpan={4}>{t('labels.total')}</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{`$${toNumberWithCommas(totalAmount)}`}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
}
